import VimeoPlayer from '@vimeo/player'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { GridFlex } from './Containers'

export const Vimeo = forwardRef(
  (
    {
      code,
      url,
      autoplay = false,
      loop = true,
      controls = false,
      contain = false,
      iframeStyles = {},
      ...props
    },
    ref
  ) => {
    const containerRef = useRef()

    const player = useVimeoPlayer(containerRef, {})
    useImperativeHandle(ref, () => {
      return player
    })

    useLayoutEffect(() => {
      if (!contain) {
        return
      }
      const container = containerRef.current
      if (!container) {
        return
      }

      const rect = container.getBoundingClientRect()
      // Find out the aspect ratio of the container
      const aspectRatio =
        rect.width > rect.height
          ? rect.width / rect.height
          : rect.height / rect.width
      container.style.transform = `scale(${aspectRatio * 1})`
    }, [contain, url])

    const dnt =
      typeof window !== 'undefined' &&
      window.navigator &&
      window.navigator.doNotTrack === '1'

    const attrs = {
      id: code || url
    }

    if (code) {
      attrs['data-vimeo-id'] = code
    } else if (url) {
      attrs['data-vimeo-url'] = url
    }

    return (
      <GridFlex
        ref={containerRef}
        id={code}
        {...attrs}
        data-vimeo-autoplay={autoplay}
        data-vimeo-background={autoplay}
        data-vimeo-playsinline
        data-vimeo-dnt={dnt}
        data-vimeo-muted={true}
        data-vimeo-loop={loop}
        data-vimeo-controls={controls}
        data-vimeo-height={1}
        width="100%"
        minWidth={0}
        alignItems="center"
        justifyContent="center"
        css={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: 0,
          iframe: {
            position: 'absolute',
            inset: 0,
            width: '100% !important',
            height: '100% !important',
            maxWidth: '100%',
            border: 0,
            boxSizing: 'border-box',
            ...iframeStyles
          }
        }}
        {...props}
      />
    )
  }
)

export const useVimeoPlayer = (containerRef, playerOptions = {}) => {
  const [vimeoPlayer, setVimeoPlayer] = useState()

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    // Get the container's width
    const rect = container.getBoundingClientRect()
    const width = rect.width

    const allOptions = {
      ...playerOptions,
      width
    }

    setVimeoPlayer(new VimeoPlayer(container, allOptions))
  }, [containerRef, playerOptions])

  return vimeoPlayer
}

export const useVimeoPlayOnHover = vimeoPlayer => {
  const containerRef = useRef()

  useEffect(() => {
    if (!vimeoPlayer) {
      return
    }

    const container = containerRef.current
    if (!container) {
      return
    }

    let playing = false

    const onPointerEnter = () => {
      if (playing) {
        return
      }

      vimeoPlayer.play().then(() => {
        playing = true
      })
    }

    const onPointerLeave = () => {
      if (!playing) {
        return
      }

      vimeoPlayer.pause().then(() => {
        playing = false
      })
    }

    container.addEventListener('pointerenter', onPointerEnter)
    container.addEventListener('pointerleave', onPointerLeave)

    return () => {
      container.removeEventListener('pointerenter', onPointerEnter)
      container.removeEventListener('pointerleave', onPointerLeave)
    }
  }, [vimeoPlayer])

  return containerRef
}

export const ArticleVimeo = ({
  width = '100%',
  maxWidth,
  aspectRatio,
  url,
  code,
  height = '100%',
  ...props
}) => (
  <GridFlex
    mx="auto"
    flexDirection="column"
    style={{
      aspectRatio
    }}
    backgroundColor="#F5F5F7"
    css={{
      borderRadius: 12,
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}
    width={width}
    maxWidth={maxWidth ?? '100%'}
    minWidth={0}
  >
    <Vimeo
      url={url}
      code={code}
      width="100%"
      height={height}
      flex="1 1 auto"
      minWidth={0}
      minHeight={0}
      {...props}
    />
  </GridFlex>
)
